import React, { memo, useCallback, useEffect, useState } from 'react'
import { concatStyles } from 'src/utils'
import { useStyles } from './styles'
import { Pagination } from '../../../Pagination'
import { AssignmentOutlined, FilterList } from '@material-ui/icons'
import { Loading } from 'src/components/Loading'
import { isMobile } from 'src/utils/isMobile'

enum TextAlign {
  start = 'start',
  end = 'end',
  left = 'left',
  right = 'right',
  center = 'center',
  justify = 'justify',
  'match-parent' = 'match-parent'
}
interface HeaderProps {
  name?: string
  property: string
  format?: Function
  size?: number | string
  textAlign?: string
  compoment?: any
  isMobileHidden?: boolean
}

interface BodyProps {
  [key: string]: any
}

interface CustomTableProps {
  header: Array<HeaderProps>
  body: Array<BodyProps>
  className?: string
  setCurrentPage?: Function
  pages?: number
  currentPage?: number
  filterIsActive?: boolean
  onClickItem?: (item: BodyProps) => void
  totalItens?: number
  totalPages?: number
  isFetchingNextPage?: boolean
  isLoading?: boolean
  isError?: boolean
  refetch?: () => void
}

export const TableViewModeComponent: React.FC<CustomTableProps> = ({
  header,
  body,
  className,
  setCurrentPage,
  pages,
  currentPage,
  filterIsActive = false,
  isFetchingNextPage,
  isLoading = false,
  totalItens,
  onClickItem,
  isError = false,
  refetch
}) => {
  const totalItems = totalItens || (filterIsActive && !body.length && '0') || (!body.length && '0')
  const isFilteredEmpty = filterIsActive && !body.length
  const [isLocalLoading, setIsLocalLoading] = useState(false)

  const styles = useStyles()

  const getTextAlign = useCallback((property = 'start'): TextAlign => {
    return TextAlign?.[property] ? TextAlign[property] : TextAlign.start
  }, [])

  useEffect(() => {
    const customStyle = document.createElement('style')
    document.head.appendChild(customStyle)

    header.forEach(({ name }, key) => {
      if (name) {
        customStyle.sheet?.insertRule(`td:nth-of-type(${key + 1}):before { content: "${name}" }`)
      }
    })

    return () => {
      if (document?.head?.lastChild) {
        document.head.removeChild(document?.head?.lastChild)
      }
    }
  }, [header])

  useEffect(() => {
    if (currentPage !== undefined) {
      const mainContainer = document.getElementById('main-container')
      if (mainContainer) mainContainer.scrollTop = 0

      if (refetch && !isFetchingNextPage) {
        setIsLocalLoading(true)

        const timer = setTimeout(() => {
          setIsLocalLoading(false)
        }, 800)

        return () => clearTimeout(timer)
      }
    }
  }, [currentPage, isFetchingNextPage, refetch])

  if ((isFetchingNextPage || isLocalLoading || isLoading || isError) && isMobile()) {
    return (
      <div className={styles.loadingContainer}>
        <Loading isError={isError} refetch={refetch} />
      </div>
    )
  }

  return (
    <>
      <div
        className={concatStyles(
          styles.tableContainer,
          `${className || ''} ${body.length === 0 ? styles.noHoverEffect : ''}`
        )}
      >
        <table>
          <thead>
            <tr>
              {header.map(({ name, size, textAlign, isMobileHidden }, key) => (
                <th
                  key={name ? name : `custom-head-${key}`}
                  style={{ width: size ? size : '', textAlign: getTextAlign(textAlign) }}
                  className={isMobileHidden ? styles.mobileHidden : ''}
                >
                  {name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isFetchingNextPage || isLocalLoading || isLoading || isError ? (
              <>
                <tr>
                  <td colSpan={header.length} className={styles.fullWidthCell} style={{ padding: 0, border: 'none' }}>
                    <div className={styles.loadingContainer}>
                      <Loading isError={isError} refetch={refetch} />
                    </div>
                  </td>
                </tr>
                <tr style={{ visibility: 'hidden', height: 0 }}>
                  {header.map(({ size }, key) => (
                    <td key={key} style={{ width: size || 'auto', padding: 0, border: 'none' }}></td>
                  ))}
                </tr>
              </>
            ) : body.length ? (
              body.map((row, rowKey) => (
                <tr
                  key={rowKey}
                  onClick={() => onClickItem && onClickItem(row)}
                  style={{ cursor: onClickItem ? 'pointer' : 'default' }}
                >
                  {header.map(({ name, property, format, textAlign, compoment, isMobileHidden }, columnKey) =>
                    compoment ? (
                      <td
                        key={`${rowKey}-${columnKey}`}
                        className={isMobileHidden ? styles.mobileHidden : ''}
                        style={{ textAlign: getTextAlign(textAlign) }}
                      >
                        {compoment(row)}
                      </td>
                    ) : (
                      <td
                        key={name}
                        className={isMobileHidden ? styles.mobileHidden : ''}
                        style={{ textAlign: getTextAlign(textAlign) }}
                      >
                        {format ? format(row?.[property]) : row?.[property]}
                      </td>
                    )
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={header.length}
                  className={styles.fullWidthCell}
                >
                  <div className={styles.notFoundItemsMsg}>
                    <div className={styles.emptyStateIcon}>
                      {isFilteredEmpty ? (
                        <FilterList style={{ fontSize: 64, opacity: 0.5, transform: 'rotate(180deg)' }} />
                      ) : (
                        <AssignmentOutlined style={{ fontSize: 64, opacity: 0.5 }} />
                      )}
                    </div>
                    <div className={styles.emptyStateContent}>
                      {isFilteredEmpty ? (
                        <>
                          <h3 className={styles.emptyStateTitle}>Nenhum resultado encontrado</h3>
                          <p className={styles.emptyStateDescription}>Sua pesquisa não retornou resultados.</p>
                          <p className={styles.emptyStateSuggestion}>
                            Tente ajustar ou limpar os filtros para ver mais registros.
                          </p>
                        </>
                      ) : (
                        <>
                          <h3 className={styles.emptyStateTitle}>Nenhum registro encontrado</h3>
                          <p className={styles.emptyStateDescription}>Não há registros para exibir.</p>
                          {/* <p className={emptyStateSuggestion}>
                            Que tal criar o primeiro registro?
                          </p> */}
                        </>
                      )}
                    </div>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {!isError && body.length > 0 && (
        <Pagination setCurrentPage={setCurrentPage} pages={pages} totalItens={totalItems} currentPage={currentPage} />
      )}
    </>
  )
}

export const TableViewMode = memo(TableViewModeComponent)
